"use client";
import classNames from "classnames";
import React, { useEffect, useMemo, useRef, useState } from "react";
import useSuggester from "../../common/utils/useSuggester";
import { useTranslations } from "next-intl";
import Loading from "../../common/components/Loading";

const SearchResultWrapper = ({
  onClose,
  searchedTerm,
  showSearch,
  contentSuggestions,
  categorySuggestions,
  typeAheadSuggestions,
  productSuggestions,
  children,
  isLoading,
}) => {
  const t = useTranslations();
  const dropdownRef = useRef(null);
  const {
    getPreviousSearchStrings,
    getPreviousVisitedProducts,
    getPreviousVisitedContent,
    getPreviousVisitedCategories,
    clearSearchHistory,
  } = useSuggester();

  const [refresh, setRefresh] = useState(true);

  // Memoized values
  const memoizedVisitedProducts = useMemo(
    () => getPreviousVisitedProducts(),
    [getPreviousVisitedProducts, refresh],
  );
  const memoizedVisitedContent = useMemo(
    () => getPreviousVisitedContent(),
    [getPreviousVisitedContent, refresh],
  );
  const memoizedPreviousSearch = useMemo(
    () => getPreviousSearchStrings(),
    [getPreviousSearchStrings, refresh],
  );
  const memoizedVisitedCategories = useMemo(
    () => getPreviousVisitedCategories(),
    [getPreviousVisitedCategories, refresh],
  );

  const hasHistory =
    memoizedPreviousSearch?.length ||
    memoizedVisitedContent?.length ||
    memoizedVisitedCategories?.length ||
    memoizedVisitedProducts?.length;
  const hasResult =
    productSuggestions?.length ||
    contentSuggestions?.length ||
    categorySuggestions?.length ||
    typeAheadSuggestions?.length;
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !event.target.closest(".search-bar")
      ) {
        onClose();
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  if (
    (!hasHistory && !searchedTerm && !showSearch) ||
    (!searchedTerm && showSearch && !hasHistory)
  )
    return null;

  return (
    <>
      <div
        className={classNames(
          "search-suggestion-dropdown-overlay fade-in-out",
          { visible: showSearch, hidden: !showSearch },
        )}
      />
      <div
        className={classNames(
          "position-absolute color-bg-white search-focused shadow",
          { visible: showSearch, hidden: !showSearch },
        )}
        ref={dropdownRef}
      >
        <div
          className="container container-narrow search-container"
          data-search-suggester="search-suggestions"
        >
          {isLoading ? (
            <Loading />
          ) : (
            children({
              memoizedPreviousSearch,
              memoizedVisitedCategories,
              memoizedVisitedProducts,
              memoizedVisitedContent,
              contentSuggestions,
              categorySuggestions,
              typeAheadSuggestions,
              productSuggestions,
              searchedTerm,
            })
          )}
          {!!hasHistory && !searchedTerm && (
            <button
              onClick={() => {
                clearSearchHistory();
                setRefresh(!refresh);
              }}
              type="button"
              className="fs-7 no-button link d-block mt-3"
            >
              {t("clear-search-history")}
            </button>
          )}
        </div>

        {!hasResult && searchedTerm && !isLoading ? (
          <p className="text-center no-result">{t("no-result")}</p>
        ) : null}
      </div>
    </>
  );
};

export default React.memo(SearchResultWrapper);
